import { epochSecondsToDate, z, type ZodType } from '@customerio/hydra-validation';
import type { Collection as UIAPICollection } from '@customerio/hydra-ui-api';
import { defineEntity } from '../shared/define-entity';

/* ------------------------------------------------------------------------- */
/* Type                                                                       */
/* ------------------------------------------------------------------------- */

export type Collection = Omit<UIAPICollection, 'id' | 'created_at' | 'updated_at'> & {
  id: string;
  created_at: Date;
  updated_at: Date;
};

/* ------------------------------------------------------------------------- */
/* Schema — only the transforms                                               */
/* ------------------------------------------------------------------------- */

// services types these as non-nullable `string[]` but sends null for a collection that has
// never been imported, so the declared Collection type only holds once null is coerced away.
const attributeList = z
  .array(z.string())
  .nullish()
  .transform((attributes) => attributes ?? []);

// oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
const CollectionFromApi = z.looseObject({
  id: z.coerce.string(),
  created_at: epochSecondsToDate,
  updated_at: epochSecondsToDate,
  displayed_attributes: attributeList,
  schema: attributeList,
}) as unknown as ZodType<Collection, unknown>;

/* ------------------------------------------------------------------------- */
/* Entity                                                                     */
/* ------------------------------------------------------------------------- */

/** @public */
export const collection = defineEntity<Collection, never, void, undefined, 'workspace'>({
  name: 'collection',
  scope: 'workspace',
  pk: (c) => c.id,
  schema: { read: CollectionFromApi },
});

export const collectionKeys = collection.keys;

/**
 * Prefix for a collection's item queries, one level under its detail key so the pages and
 * filter variants can be invalidated together — and so deleting the collection drops them
 * with it. The per-request params are appended by the query options.
 */
export const collectionItemsKey = (workspaceId: number, id: string) =>
  [...collectionKeys.detail(workspaceId, id), 'items'] as const;

export function parseCollection(raw: unknown): Collection {
  return collection.parse(raw);
}

/* ------------------------------------------------------------------------- */
/* Selectors                                                                  */
/* ------------------------------------------------------------------------- */

export function importIsInvalid(c: Pick<Collection, 'importing' | 'import_error' | 'rows'>): boolean {
  return !c.importing && (c.import_error?.length ?? 0) > 0 && c.rows === 0;
}
